import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { IMappingFormSettingsResponse } from 'wisestep-api';

import RenderTextInput from '../../components/PersonalInfo/RenderTextInput';
import ISubmissionSettingContext from '../../types/ISubmissionSettingContext';
import ISubmissionSettingsResponse from '../../types/ISubmissionSettingsResponse';
import { ControlledTextInput } from '../ControlledTextInput';
import { Item } from '../Item';
import { Label } from '../Label';
import { TagsInput } from '../TagsInput';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

export interface IWorkExpForm {
  showTitle?: boolean;
  submissionSetting?: ISubmissionSettingsResponse | ISubmissionSettingContext | null;
  mappingFormSettings?: IMappingFormSettingsResponse;
  purpose?: 'submission' | 'mapping';
}
const formLabel = (label: string, isRequired: boolean) => label + (isRequired ? '*' : '');

const WorkExpForm: FC<IWorkExpForm> = ({
  showTitle = true,
  submissionSetting,
  purpose = 'submission',
  mappingFormSettings,
}) => {
  const { watch } = useFormContext();
  return (
    <>
      {showTitle && (
        <GridItem sx={{ mt: '12px' }}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
            Work Experience
          </WsTypography>
        </GridItem>
      )}
      {purpose === 'submission' && (
        <>
          {!submissionSetting?.totalExperience?.hidden && (
            <>
              <GridItem>
                <Label>
                  {formLabel(
                    'Total Experience ',
                    submissionSetting?.totalExperience?.required ?? false
                  )}
                </Label>
              </GridItem>
              <Grid container>
                <Grid item xs={4.25}>
                  <RenderTextInput
                    name="expInYears"
                    labelText=""
                    placeholder="Enter Years"
                    type="number"
                  />
                </Grid>
                <Grid item xs={4.25}>
                  <RenderTextInput
                    name="expInMonth"
                    labelText=""
                    placeholder="Enter Month"
                    type="number"
                  />
                </Grid>
              </Grid>
            </>
          )}
          {!submissionSetting?.relevantExperience?.hidden && (
            <>
              <GridItem>
                <Label>
                  {formLabel(
                    'Relevant Experience ',
                    submissionSetting?.relevantExperience?.required ?? false
                  )}
                </Label>
              </GridItem>
              <Grid container>
                <Grid item xs={4.25}>
                  <RenderTextInput
                    name="relExpInYears"
                    labelText=""
                    placeholder="Enter Years"
                    type="number"
                  />
                </Grid>
                <Grid item xs={4.25}>
                  <RenderTextInput
                    name="relExpInMonth"
                    labelText=""
                    placeholder="Enter Month"
                    type="number"
                  />
                </Grid>
              </Grid>
            </>
          )}
          {!submissionSetting?.currentJobTitle?.hidden && (
            <RenderTextInput
              name="jobTitle"
              labelText={formLabel(
                'Current Job Title ',
                submissionSetting?.currentJobTitle?.required ?? false
              )}
              placeholder="Enter Current Job Title"
            />
          )}
          {!submissionSetting?.currentEmployer?.hidden && (
            <RenderTextInput
              name="currentCompany"
              labelText={formLabel(
                'Current Employer ',
                submissionSetting?.currentEmployer?.required ?? false
              )}
              placeholder="Enter Current Employer"
            />
          )}
          {!submissionSetting?.previousJobTitles?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Previous Job Titles ',
                    submissionSetting?.previousJobTitles?.required ?? false
                  )}
                </Label>
                <FormTagsInput
                  name="prevJobTitles"
                  placeholder="Enter previous job titles"
                  multiple={true}
                  initialValue={watch('prevJobTitles') ?? []}
                />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.previousEmployers?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Previous Employers ',
                    submissionSetting?.previousEmployers?.required ?? false
                  )}
                </Label>
                <FormTagsInput
                  name="prevEmployers"
                  placeholder="Enter previous employers"
                  multiple={true}
                  initialValue={watch('prevEmployers') ?? []}
                />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.highestEducation?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Highest Education Qualifications ',
                    submissionSetting?.highestEducation?.required ?? false
                  )}
                </Label>
                <FormTagsInput
                  name="highestEducation"
                  placeholder="Enter qualifications"
                  multiple={true}
                  initialValue={watch('highestEducation') ?? []}
                />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.institutesAttended?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Institutions Attended ',
                    submissionSetting?.institutesAttended?.required ?? false
                  )}
                </Label>
                <FormTagsInput
                  name="institutesAttended"
                  placeholder="Enter institutions"
                  multiple={true}
                  initialValue={watch('institutesAttended') ?? []}
                />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.certifications?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Certifications ',
                    submissionSetting?.certifications?.required ?? false
                  )}
                </Label>
                <FormTagsInput
                  name="certifications"
                  placeholder="Enter certifications"
                  multiple={true}
                  initialValue={[]}
                />
              </Grid>
            </GridItem>
          )}
        </>
      )}
      {purpose === 'mapping' && (
        <>
          {mappingFormSettings?.totalExperience.show && (
            <>
              <GridItem>
                <Label>
                  {formLabel('Total experience', mappingFormSettings?.totalExperience.mandatory)}
                </Label>
              </GridItem>
              <Grid container>
                <Grid item xs={6}>
                  <GridItem>
                    <ControlledTextInput
                      name="expInYears"
                      type="number"
                      fullWidth
                      id="candidate_exp_in_year"
                      placeholder={'Enter Years'}
                      variant="outlined"
                      autoComplete="candidate_exp_in_year"
                      required={mappingFormSettings?.totalExperience.mandatory}
                    />
                  </GridItem>
                </Grid>
                <Grid item xs={6}>
                  <GridItem>
                    <ControlledTextInput
                      name="expInMonth"
                      type="number"
                      fullWidth
                      id="candidate_exp_in_month"
                      placeholder="Enter Month"
                      variant="outlined"
                      autoComplete="candidate_exp_in_month"
                      required={mappingFormSettings?.totalExperience.mandatory}
                    />
                  </GridItem>
                </Grid>
              </Grid>
            </>
          )}
          {mappingFormSettings?.relevantExperience.show && (
            <>
              <GridItem>
                <Label>
                  {formLabel('Relevant experience', mappingFormSettings?.totalExperience.mandatory)}
                </Label>
              </GridItem>
              <Grid container>
                <Grid item xs={6}>
                  <GridItem>
                    <ControlledTextInput
                      name="relExpInYears"
                      type="number"
                      fullWidth
                      id="candidate_exp_in_year"
                      placeholder="Enter Years"
                      variant="outlined"
                      autoComplete="candidate_exp_in_year"
                      required={mappingFormSettings?.relevantExperience.mandatory}
                    />
                  </GridItem>
                </Grid>
                <Grid item xs={6}>
                  <GridItem>
                    <ControlledTextInput
                      name="relExpInMonth"
                      type="number"
                      fullWidth
                      id="candidate_exp_in_month"
                      placeholder="Enter Month"
                      variant="outlined"
                      autoComplete="candidate_exp_in_month"
                      required={mappingFormSettings?.relevantExperience.mandatory}
                    />
                  </GridItem>
                </Grid>
              </Grid>
            </>
          )}
          {mappingFormSettings?.jobTitle.show && (
            <GridItem>
              <ControlledTextInput
                name="jobTitle"
                labelText={formLabel('Current Job Title', mappingFormSettings.jobTitle.mandatory)}
                fullWidth
                id="candidate_jobTitle"
                placeholder="Enter Current Job Title"
                variant="outlined"
                autoComplete="candidate_jobTitle"
                required={mappingFormSettings.jobTitle.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.currentCompany.show && (
            <GridItem>
              <ControlledTextInput
                name="currentCompany"
                labelText={formLabel(
                  'Current Employer',
                  mappingFormSettings?.currentCompany.mandatory
                )}
                fullWidth
                id="candidate_currentCompany"
                placeholder="Enter Current Employer"
                variant="outlined"
                autoComplete="candidate_currentCompany"
                required={mappingFormSettings?.currentCompany.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.previousJobTitles.show && (
            <GridItem>
              <Label>
                {formLabel('Previous Job Titles', mappingFormSettings?.previousJobTitles.mandatory)}
              </Label>
              <FormTagsInput
                name="prevJobTitles"
                placeholder="Enter previous job titles"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.previousJobTitles.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.prevEmployers.show && (
            <GridItem>
              <Label>
                {formLabel('Previous Employers', mappingFormSettings?.prevEmployers.mandatory)}
              </Label>
              <FormTagsInput
                name="prevEmployers"
                placeholder="Enter previous employers"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.prevEmployers.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.highestEducation.show && (
            <GridItem>
              <Label>
                {formLabel(
                  'Highest education qualifications',
                  mappingFormSettings?.highestEducation.mandatory
                )}
              </Label>
              <FormTagsInput
                name="highestEducation"
                placeholder="Enter qualifications"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.highestEducation.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.institutesAttended.show && (
            <GridItem>
              <Label>
                {formLabel(
                  'Institutions Attended',
                  mappingFormSettings?.institutesAttended.mandatory
                )}
              </Label>
              <FormTagsInput
                name="institutesAttended"
                placeholder="Enter institutions"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.institutesAttended.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.certifications.show && (
            <GridItem>
              <Label>
                {formLabel('Certifications', mappingFormSettings?.certifications.mandatory)}
              </Label>
              <FormTagsInput
                name="certifications"
                placeholder="Enter certifications"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.certifications.mandatory}
              />
            </GridItem>
          )}
        </>
      )}
    </>
  );
};

export default WorkExpForm;
